import React, { FC, memo, useCallback, useEffect } from "react"
import { Appearance, ScrollView, ViewStyle } from "react-native"
import { observer } from "mobx-react-lite"
import { useStores } from "../../models"
import { Modal, useDisclose, View } from "native-base"
import { Text } from "../text/text"
import { nativeBaseTheme } from "../../theme"
import { Image } from "../image/image"
import { Button } from "../button/button"
import { exitWalkthrough, startWalkthrough } from "react-native-walkthrough"
import homeWalkthrough from "../../guides/home-walkthrough"
import { useIsFocused } from "@react-navigation/native"

export interface OnboardWelcomeModalProps {
  testID?: string | undefined,
  colorScheme?: "light"|"dark",
  scrollViewRef?: { current: ScrollView }
}

const onboardWelcome = require('./onboard-welcome.png')

const ONBOARD_CONSULTATION_IMAGE = {
  height: 163,
  width: 215
}

const TOP_BUTTON: ViewStyle = { marginBottom: 12 }

export const OnboardWelcomeModal: FC<OnboardWelcomeModalProps> = memo(observer(function OnboardWelcomeModal(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    scrollViewRef
  } = props;
  const { userStore } = useStores()
  const { isOpen, onClose: onCloseModal } = useDisclose(true)
  const isFocused = useIsFocused()

  const onClose = useCallback(() => {
    startWalkthrough(homeWalkthrough(() => {
      userStore.set("isCoachmarkRunning", true)
      userStore.updateData({ state: { home_coachmark_shown: true } })
        .finally(() => userStore.getUser())
      if (scrollViewRef && scrollViewRef.current)
        scrollViewRef.current.scrollTo({ y: 0 })
    }));
    onCloseModal();
  }, [onCloseModal, scrollViewRef, userStore])

  const onSkip = useCallback(() => {
    userStore.set("skipCoachmark", true)
    if (scrollViewRef && scrollViewRef.current)
        scrollViewRef.current.scrollTo({ y: 0 })
    onCloseModal();
  }, [onCloseModal, scrollViewRef, userStore])

  useEffect(() => {
    return () => {
      if (isFocused) {
        setTimeout(() => {
          exitWalkthrough()
          userStore.set("isCoachmarkRunning", false)
        }, 1000)
      }
    }
  }, [isFocused, userStore])

  return <Modal
    testID="jala:id/onboard-welcome-modal"
    isOpen={isOpen}
    onClose={onClose}
  >
    <View
      alignSelf="stretch"
      marginY="80px"
      marginX="16px"
      padding="16px"
      rounded="8px"
      backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
    >
      <View alignItems="center">
        <Image source={onboardWelcome} style={ONBOARD_CONSULTATION_IMAGE} />
      </View>
      <Text preset="title1" tx="onboardModal.welcomeTitle" marginTop="16px" marginBottom="8px" textAlign="center" colorScheme={colorScheme} />
      <Text preset="body2" tx="onboardModal.welcomeDescription" minHeight="60px" marginBottom="8px" textAlign="center" colorScheme={colorScheme} />
      <Button
        testID="jala:id/action-button"
        tx="onboardModal.welcomeAction"
        preset="primary"
        colorScheme={colorScheme}
        onPress={onClose}
        style={TOP_BUTTON}
      />
      <Button
        testID="jala:id/skip-action-button"
        tx="onboardModal.welcomeSkipAction"
        preset="outline"
        colorScheme={colorScheme}
        onPress={onSkip}
      />
    </View>
  </Modal>;
}))
